import { trackingPartLength } from './limits';
export const DEFAULT_DATE_MASK = 'YYYYMMDD';
export const DEFAULT_FIELD_DELIMITER = '_';
export const DEFAULT_SUBFIELD_DELIMITER = '-';
/**
 * Worst-case length a linked join key contributes when it is opted into the
 * name. The real length depends on the target schema's key, which is not
 * resolved here, so this is a deliberate over-estimate (a code like
 * `NIKE-2026-A7X9Q`).
 */
export const LINKED_KEY_ESTIMATE = 16;
/**
 * The mask a date PART falls back to when it declares none. The mint reads this
 * very constant — `formatPartDate` is `renderDateMask(date, mask ?? DEFAULT_PART_DATE_MASK)`
 * — so the agreement is structural rather than a coincidence two files have to
 * keep noticing. The generic fallback below happens to be 4 as well, and nothing
 * ties those two together.
 *
 * Note this is NOT `DEFAULT_DATE_MASK`: a date FIELD's mask is written by the
 * date picker and defaults to `YYYYMMDD`, a date PART's is written by the
 * tracking-code composer and defaults to `YYYY`. Two settings, two runtimes.
 */
export const DEFAULT_PART_DATE_MASK = 'YYYY';
/**
 * What a token is worth when nothing can be measured — a field part pointing at
 * a field that does not exist, say. Deliberately an over-estimate: the counter
 * gating a channel limit should not under-report.
 */
const UNMEASURABLE_TOKEN = 4;
/** A slot with no controlled code still occupies the name; one character is the floor. */
const EMPTY_VOCABULARY_TOKEN = 1;
/**
 * Whether a field contributes to the concatenated name. Everything does except
 * a `linked` field that has not opted in — a join key is a stored reference by
 * default, not part of the string.
 */
export function isFieldInName(field) {
    if (field.type !== 'linked')
        return true;
    return !!field.link?.includeInName;
}
/**
 * The longest token this field's own vocabulary can produce.
 *
 * A blank code falls back to the display value, because that is what the name
 * generator emits — and it applies to inline vocabularies as well as
 * list-backed ones. Measuring only non-blank codes let a row with a label and
 * no code count as nothing, while the generated name carried the whole label.
 */
function vocabularyTokenLength(field, useDisplayValue = false) {
    const lengths = field.values
        .map((v) => (useDisplayValue ? v.value : v.code || v.value).length)
        .filter((n) => n > 0);
    return lengths.length > 0 ? Math.max(...lengths) : EMPTY_VOCABULARY_TOKEN;
}
/** The mask a date field actually renders with, honouring a planned change. */
function dateMaskLength(field, options) {
    const planned = options.planned?.formats?.get(field.id);
    if (planned !== undefined)
        return planned.length;
    // NOT trimmed: the date picker writes `formatDateMask(date, mask)` verbatim,
    // so a padded mask really does produce a padded token. `||` rather than `??`
    // so an empty mask falls back to the default the picker would use.
    return field.format?.length || DEFAULT_DATE_MASK.length;
}
/**
 * Worst-case length of one part of a Variable field's pattern.
 *
 * Each kind has its OWN default, matching what the generator emits when the
 * property is absent — a `sequence` with no declared width is padded to 5, a
 * `random` to 6. A single shared fallback made every part worth 4 and, worse,
 * read a `length` belonging to a different kind of part.
 */
function patternPartLength(part, byId, options) {
    switch (part.kind) {
        case 'literal':
            return part.text?.length ?? 0;
        case 'date':
            // `??`, not `||`, and that is deliberate: `renderDateMask` has no fallback
            // of its own, so a mask cleared to '' really does emit nothing, while an
            // absent one takes the default. `formatPartDate` draws the same
            // distinction, which is why the two agree by construction. A date FIELD is
            // the opposite case — its `||` collapses both — see `dateMaskLength`.
            return part.format?.length ?? DEFAULT_PART_DATE_MASK.length;
        case 'sequence':
        case 'random':
            return trackingPartLength(part);
        case 'field': {
            const ref = part.fieldId ? byId.get(part.fieldId) : undefined;
            if (!ref)
                return UNMEASURABLE_TOKEN;
            /**
             * A field part contributes exactly what that field's own COLUMN
             * contributes, so this routes by the REFERENCED field's kind.
             *
             * The mint looks the chosen value up in a vocabulary and falls through to
             * the raw value when it misses — and a date, linked or computed field has
             * no `values`, so the lookup ALWAYS misses. Measuring those as a
             * one-character vocabulary miss under-reported a date reference by 7 and a
             * linked one by 15, on the number that gates the platform character caps.
             */
            if (ref.type === 'date')
                return dateMaskLength(ref, options);
            if (ref.type === 'linked')
                return LINKED_KEY_ESTIMATE;
            /**
             * A Variable referencing another Variable is UNMEASURABLE — the same
             * answer as a reference that resolves to nothing at all.
             *
             * Not because the generator emits nothing for it. It emits whatever the
             * CALLER supplied for that field id: `resolveTrackingPart` reads the
             * chosen values, and the vocabulary lookup misses because a computed field
             * has no `values`, so it falls through to the raw supplied string. Nothing
             * filters computed ids out of the chosen map, so an API caller can put an
             * arbitrary 255-character value there.
             *
             * That is exactly why it is unmeasurable rather than measurable-as-zero.
             * Every other kind is predictable because the builder constrains what gets
             * sent — a date picker writes its mask, a dropdown writes one of its
             * codes. There is no builder path that creates this reference at all, so
             * there is no constrained input to predict, and a number invented for it
             * would be a guess dressed as a measurement.
             *
             * Recursing into the referenced pattern was not that number either: the
             * generator never assembles the inner Variable, so the walk measured a
             * width nothing emits. It also cost more than it looked — a visited set
             * that blocks ancestors only terminates, but walks a legal acyclic chain
             * once per PATH through it, and a 5-deep chain at 25 parts pinned the
             * event loop for a second, in the browser as well as on the API. Nothing
             * memoisable fixes that: a value cut by the visited set is unsafe to
             * cache, and one all-to-all cycle taints every node.
             *
             * The write path refuses the shape outright — the DTO decorator on a
             * single request, and `updateDraft` on the merged version, since a partial
             * save can carry one half of the pair at a time. So this is the answer for
             * stored data that predates those guards, not for anything newly authored.
             */
            if (ref.type === 'computed')
                return UNMEASURABLE_TOKEN;
            /**
             * Vocabulary-backed kinds only. `source` changes the answer here and
             * nowhere else, because for the kinds above the mint ignores it entirely.
             *
             * The projection has to reach the REFERENCED field, not the Variable that
             * names it: a projection that missed it reported a shortfall the plan had
             * already closed, after spending levers to close it.
             */
            if (part.source !== 'value') {
                const planned = part.fieldId ? options.planned?.codeLengths?.get(part.fieldId) : undefined;
                if (planned !== undefined)
                    return planned;
                const resolved = part.fieldId ? options.resolvedCodeLength?.get(part.fieldId) : undefined;
                if (resolved !== undefined)
                    return resolved;
            }
            return vocabularyTokenLength(ref, part.source === 'value');
        }
        default:
            return 0;
    }
}
/**
 * Index of fields by id, built ONCE per estimate and threaded down.
 *
 * It used to be rebuilt inside every call, which is O(fields) per Variable and
 * measured 8-10x at the 500-field cap — independent of anything else.
 */
const indexById = (fields) => new Map(fields.map((f) => [f.id, f]));
/**
 * The index is threaded through the INTERNAL pair and never exposed.
 *
 * An optional `byId` parameter on the exported functions would be two sources of
 * one truth: a caller could pass a map built from a different array than the
 * `fields` beside it, and the two would silently disagree. The exported
 * signatures keep taking `fields` and build the index once.
 */
function computedTokenLengthWith(field, byId, options) {
    return (field.pattern ?? []).reduce((sum, part) => sum + patternPartLength(part, byId, options), 0);
}
function fieldTokenLengthWith(field, byId, options) {
    if (field.type === 'computed')
        return computedTokenLengthWith(field, byId, options);
    if (field.type === 'linked')
        return LINKED_KEY_ESTIMATE;
    if (field.type === 'date')
        return dateMaskLength(field, options);
    const planned = options.planned?.codeLengths?.get(field.id);
    if (planned !== undefined)
        return planned;
    const resolved = options.resolvedCodeLength?.get(field.id);
    if (resolved !== undefined)
        return resolved;
    return vocabularyTokenLength(field);
}
/**
 * Worst-case length of a Variable field's whole pattern.
 *
 * Flat by construction: a pattern's parts are measured directly and a reference
 * to another Variable is unmeasurable rather than followed, so this reads every
 * part exactly once.
 */
export function computedTokenLength(field, fields, options = {}) {
    return computedTokenLengthWith(field, indexById(fields), options);
}
/** Worst-case length of ONE field's token, whatever kind it is. */
export function fieldTokenLength(field, fields, options = {}) {
    return fieldTokenLengthWith(field, indexById(fields), options);
}
/**
 * How many times shortening this field's codes shortens the generated name.
 *
 * Usually 1. But a field referenced by a Variable column appears twice — once
 * as its own column and once inside the Variable — so a character cut from it
 * removes two from the name. The shortening planner credits one character per
 * character by default, which made it over-shorten: in one measured case it
 * destroyed a date mask's year to close a gap a non-lossy mask had already
 * closed exactly.
 *
 * Zero is a real answer: a field that contributes to no name at all (an
 * excluded linked field, or a subfield of one) is not worth shortening.
 */
export function nameOccurrences(field, fields) {
    const inName = fields.filter(isFieldInName);
    const placed = new Set(measuredFieldIds(inName));
    const own = placed.has(field.id) ? 1 : 0;
    const referenced = inName
        .filter((f) => f.type === 'computed' && placed.has(f.id))
        .reduce((n, f) => n +
        (f.pattern ?? []).filter(
        // A `source: 'value'` part emits the DISPLAY value, which the mint
        // returns before any vocabulary lookup — so shortening this field's
        // CODES cannot change that part's width. Counting it told the planner
        // a cut was worth two characters when only one was real, which is the
        // same over-shortening the weight was introduced to prevent.
        (p) => p.kind === 'field' && p.fieldId === field.id && p.source !== 'value').length, 0);
    return own + referenced;
}
/**
 * The fields the name is actually built from, in order.
 *
 * Excluded linked fields are dropped BEFORE grouping, so neither they nor their
 * delimiters are counted — measuring them and then skipping them charged a
 * separator for a token that never appears. A field whose parent is not itself
 * placed contributes nothing, which is why this returns ids rather than a flat
 * filter: it is the placement rule, not a predicate.
 */
function measuredFieldIds(inName) {
    const topLevel = [
        ...inName.filter((f) => f.group === 'media' && !f.parentId),
        ...inName.filter((f) => f.group === 'content' && !f.parentId),
    ];
    const ids = [];
    for (const parent of topLevel) {
        ids.push(parent.id);
        for (const sub of inName.filter((f) => f.parentId === parent.id))
            ids.push(sub.id);
    }
    return ids;
}
/**
 * Worst-case length of the whole generated name: every placed field contributes
 * its longest token, top-level clusters joined by the field delimiter and
 * subfields by the subfield delimiter.
 *
 * This is the one number. It used to exist three times — here, in the backend's
 * shortening endpoint, and in the frontend's character counter — and the three
 * disagreed eight ways, two of them visible in the same dialog.
 */
export function estimateNameLength(fields, options = {}) {
    const fieldDelimiter = options.fieldDelimiter || DEFAULT_FIELD_DELIMITER;
    const subfieldDelimiter = options.subfieldDelimiter || DEFAULT_SUBFIELD_DELIMITER;
    const inName = fields.filter(isFieldInName);
    const topLevel = [
        ...inName.filter((f) => f.group === 'media' && !f.parentId),
        ...inName.filter((f) => f.group === 'content' && !f.parentId),
    ];
    if (topLevel.length === 0)
        return 0;
    const byId = indexById(fields);
    let total = 0;
    topLevel.forEach((parent, i) => {
        if (i > 0)
            total += fieldDelimiter.length;
        total += fieldTokenLengthWith(parent, byId, options);
        for (const sub of inName.filter((f) => f.parentId === parent.id)) {
            total += subfieldDelimiter.length + fieldTokenLengthWith(sub, byId, options);
        }
    });
    return total;
}
